import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ZoomIn, ZoomOut, Image as ImageIcon } from 'lucide-react';
import ContextMenu from './ui/ContextMenu';
import { copyImageToClipboard, downloadImage } from '../utils/clipboard';
import './Chat.css';

/**
 * Fullscreen preview overlay for chat images. Same backdrop + fade/scale-in
 * effect as ui/Modal (.modal-backdrop / fadeIn in index.css). Closes on
 * backdrop click, the X button, or Escape (captured so underlying panels
 * don't also react).
 *
 * Toolbar: zoom toggle, download (native save dialog under Tauri, anchor
 * download on the web), close. Zoom magnifies the image 2x inside the exact
 * box the fitted image occupied, so it never overflows the app window.
 * Clicking the image zooms in centered on the clicked spot; while zoomed the
 * image can be dragged to pan, and a plain click (no drag) zooms back out.
 * Right-clicking the image offers Copy image / Download.
 */
export function ImageLightbox({ src, alt, onClose }) {
  // The fitted image's rendered box {w, h} plus the zoom focus point as
  // fractions of the image {fx, fy}; non-null while zoomed.
  const [zoomBox, setZoomBox] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y }
  const imgRef = useRef(null);
  const zoomScrollRef = useRef(null);
  // In-flight drag: pointer start + scroll origin, and whether it moved far
  // enough to count as a pan rather than a click.
  const dragRef = useRef(null);
  const zoomed = zoomBox != null;

  // Zoom in around a point given as fractions of the image (0.5/0.5 = center).
  const zoomInAt = (fx, fy) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (rect?.width) setZoomBox({ w: rect.width, h: rect.height, fx, fy });
  };

  const toggleZoom = () => {
    if (zoomed) setZoomBox(null);
    else zoomInAt(0.5, 0.5);
  };

  const onFittedImageClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    zoomInAt((e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
  };

  // Escape closes the context menu first if one is open, then the preview.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (ctxMenu) setCtxMenu(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, ctxMenu]);

  // Start the zoomed view panned so the focus point sits mid-frame.
  useLayoutEffect(() => {
    const el = zoomScrollRef.current;
    if (!zoomBox || !el) return;
    el.scrollLeft = zoomBox.fx * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = zoomBox.fy * el.scrollHeight - el.clientHeight / 2;
  }, [zoomBox]);

  const onZoomPointerDown = (e) => {
    const el = zoomScrollRef.current;
    if (!el || e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
  };

  const onZoomPointerMove = (e) => {
    const drag = dragRef.current;
    const el = zoomScrollRef.current;
    if (!drag || !el) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) drag.moved = true;
    el.scrollLeft = drag.left - dx;
    el.scrollTop = drag.top - dy;
  };

  const onZoomPointerUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    // A click that never turned into a drag zooms back out.
    if (drag && !drag.moved) setZoomBox(null);
  };

  const onImageContextMenu = (e) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  const download = () => downloadImage(src, alt || 'image');

  return createPortal(
    <div className="chat-lightbox" role="dialog" aria-label={alt || 'Image preview'}>
      <div className="modal-backdrop chat-lightbox-backdrop" onClick={onClose} />
      <div className="chat-lightbox-toolbar">
        <button
          type="button"
          className="chat-lightbox-btn"
          title={zoomed ? 'Zoom out' : 'Zoom in'}
          onClick={toggleZoom}
        >
          {zoomed ? <ZoomOut size={18} /> : <ZoomIn size={18} />}
        </button>
        <button type="button" className="chat-lightbox-btn" title="Download" onClick={download}>
          <Download size={18} />
        </button>
        <button type="button" className="chat-lightbox-btn" title="Close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      {zoomed ? (
        <div
          ref={zoomScrollRef}
          className="chat-lightbox-zoombox"
          style={{ width: zoomBox.w, height: zoomBox.h }}
          onPointerDown={onZoomPointerDown}
          onPointerMove={onZoomPointerMove}
          onPointerUp={onZoomPointerUp}
          onPointerCancel={() => {
            dragRef.current = null;
          }}
          onContextMenu={onImageContextMenu}
        >
          <img
            className="chat-lightbox-img--zoomed"
            src={src}
            alt={alt || 'image'}
            draggable={false}
          />
        </div>
      ) : (
        <img
          ref={imgRef}
          className="chat-lightbox-img"
          src={src}
          alt={alt || 'image'}
          onClick={onFittedImageClick}
          onContextMenu={onImageContextMenu}
        />
      )}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          zIndex={100051}
          onClose={() => setCtxMenu(null)}
          items={[
            {
              icon: <ImageIcon size={14} />,
              label: 'Copy image',
              onClick: () => copyImageToClipboard(src),
            },
            { icon: <Download size={14} />, label: 'Download', onClick: download },
          ]}
        />
      )}
    </div>,
    document.body
  );
}

/** Inline image thumbnail that opens the lightbox when clicked. */
export function LightboxImage({ className, src, alt, style }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <img
        className={`${className || ''} chat-attach-img--clickable`}
        src={src}
        alt={alt}
        style={style}
        onClick={() => setOpen(true)}
      />
      {open && <ImageLightbox src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}
